// dependencies
import React, { useMemo, forwardRef, useState } from 'react'
import Cookies from 'universal-cookie'
import PropTypes from 'prop-types'
import { graphql, Link as GatsbyLink, useStaticQuery, navigate } from 'gatsby'
import { Link as MuiLink, makeStyles, Hidden } from '@material-ui/core'
import classNames from 'classnames'
import { useFeatureValue } from '@growthbook/growthbook-react'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
// helpers
import { savePageScroll } from '@helpers/product'
import { useLocation } from '@gatsbyjs/reach-router'
import { canClientSideRoute } from '@helpers/link'
import ApplyButton, { isSynchronyApplyLink, isSynchronyWithQueriesLink } from '@shared/apply-button'
import useLearnMoreModal from '@helpers/useLearnMoreModal'
import InfoModal from '@components/shared/info-modal'
import { useModalsContext } from '@context/modalContext'

const cookies = new Cookies()

const useStyles = makeStyles(theme => ({
  link: {
    '-webkit-tap-highlight-color': 'transparent',
    position: 'relative',
    '& svg.link-arrow-next': {
      position: 'absolute',
      fontSize: '1.5em',
      top: '50%',
      transform: 'translateY(-52%)',
      right: '-1.8rem',
    },
    '&.disable-underline:hover': {
      textDecoration: 'none',
    },
    [theme.breakpoints.down('md')]: {
      '&:focus': {
        outline: 'none',
      },
    },
  },
}))

const noop = () => {}
const isEmptyObject = obj => obj && Object.keys(obj).length === 0

/**
 * Retunrs GTM props and dataLayer from trackingData object
 * @param {object} trackingData
 */
const getAnalytics = (trackingData, Title) => {
  if (trackingData && typeof trackingData === 'object') {
    const { category = null, action = null, label = null, value = null, ...data } = trackingData
    return {
      GTM: {
        'gtm-category': category,
        'gtm-action': action,
        'gtm-label': category === 'navigation' ? Title : label,
        'gtm-value': value,
      },
      DataLayer: isEmptyObject(data) ? null : data,
    }
  }
  return { GTM: {}, DataLayer: null }
}

const determineText = (children, linkData = {}) => {
  if (children) return children
  const { DisplayText, MobileDisplayText, Title } = linkData || {}
  const desktopText = DisplayText ?? Title
  const mobileText = MobileDisplayText ?? DisplayText ?? Title
  return (
    <>
      <Hidden mdDown implementation="css">
        <span data-testid="desktop-link:text">{desktopText}</span>
      </Hidden>
      <Hidden lgUp implementation="css">
        <span data-testid="mobile-link:text">{mobileText}</span>
      </Hidden>
    </>
  )
}

const formatProps = (props = {}, data = {}, trackingData) => {
  const { AlternateDescription, Title, Target, Color } = data || {}
  const { onClick = noop, onKey = noop, style = {}, state = {}, ...linkProps } = props
  const analytics = getAnalytics(trackingData, Title)
  return {
    title: AlternateDescription || Title || null,
    target: Target || null,
    onClick: e => {
      if (typeof window !== 'undefined') {
        savePageScroll()
        if (analytics.DataLayer) {
          window.dataLayer.push(analytics.DataLayer)
        }
      }
      onClick(e)
    },
    onKeyDownCapture: onKey,
    style: Color?.ColorHex
      ? {
          color: Color.ColorHex,
          ...style,
        }
      : style,
    ...analytics.GTM,
    ...linkProps,
  }
}

const determinePredefinedLinkParams = (predefinedLink, externalUrl) => {
  const modOneDefaultLink = 'https://modularone.roomstogo.com/configurations'
  const modTwoDefaultLink = 'https://modulartwo.roomstogo.com/'
  const modThreeDefaultLink = 'https://modularthree.roomstogo.com/configurations'

  if (typeof window !== 'undefined') {
    const zip = cookies.get('__Host-rtg_zip')
    const rtgSessionID = cookies.get('__Host-rtgp')

    const modLinks = {
      modular_one_configurator: modOneDefaultLink,
      modular_two_configurator: modTwoDefaultLink,
      modular_three_configurator: modThreeDefaultLink,
    }

    return `${externalUrl || modLinks[predefinedLink]}${rtgSessionID && `?session=${rtgSessionID}`}${zip &&
      `&zipCode=${zip}`}&web=true`
  }
  return ''
}

const StrapiLink = ({
  data,
  disableUnderline = false,
  children = null,
  arrowNext = null,
  trackingData = null,
  className = '',
  preserveScrollOnNavigate = false,
  ...otherProps
}) => {
  const [showMattressFinanceModal, setShowMattressFinanceModal] = useState(false)
  const linkData = data?.Link || data
  const MattressFinanceModalData = useStaticQuery(MattressFinanceModalQuery)
  const linkProps = useMemo(() => formatProps(otherProps, linkData, trackingData), [otherProps, linkData, trackingData])
  const classes = useStyles(disableUnderline)
  const location = useLocation()
  const { actions } = useModalsContext()
  const growthbookLinkUrl = useFeatureValue(linkData?.CustomLinkGrowthbookId)
  const handleLinkShowModal = e => {
    if (e && e.preventDefault) e.preventDefault()
    handleShowModal()
  }
  const handleLinkModalClose = e => {
    if (e && e.preventDefault) e.preventDefault()
    handleClose()
  }
  // Allow link to open finance modal
  const { showModal, modalHtml, handleClose, handleShowModal } = useLearnMoreModal()
  if (linkData?.Title === 'Mattress Finance Modal Link') {
    return (
      <>
        <MuiLink
          className={classNames(classes.link, className, { 'disable-underline': disableUnderline })}
          {...linkProps}
          data-testid={linkData?.testId}
          style={{
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onClick={() => setShowMattressFinanceModal(true)}
          aria-label={linkData?.AlternateDescription}
        >
          {children || linkData?.DisplayText || linkData?.Title}
          {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
        </MuiLink>
        {showMattressFinanceModal && MattressFinanceModalData?.strapiHtml?.HTML && (
          <InfoModal
            label="Rooms To Go Mattress Finance Modal"
            mdlClass="rtg-mattress-finance-modal"
            shouldShowModal={showMattressFinanceModal}
            closeModal={() => setShowMattressFinanceModal(false)}
          >
            <div
              className={classes.html}
              dangerouslySetInnerHTML={{
                __html: MattressFinanceModalData?.strapiHtml?.HTML,
              }}
            />
          </InfoModal>
        )}
      </>
    )
  }
  if (linkData?.ShowLearnMoreFinanceModal) {
    return (
      <>
        <MuiLink
          component="button"
          className={classNames(classes.link, className, { 'disable-underline': disableUnderline })}
          {...linkProps}
          data-testid={linkData?.testId}
          style={{
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onClick={e => handleLinkShowModal(e)}
          aria-label={linkData?.AlternateDescription}
        >
          {children || linkData?.DisplayText || linkData?.Title}
          {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
        </MuiLink>
        {showModal && modalHtml && (
          <InfoModal
            label="Rooms To Go Credit Options"
            mdlClass="rtg-credit-modal"
            shouldShowModal={showModal}
            closeModal={e => handleLinkModalClose(e)}
          >
            <div
              className={classes.html}
              dangerouslySetInnerHTML={{
                __html: modalHtml,
              }}
              onClick={e => e.preventDefault()}
              onKeyDown={e => e.preventDefault()}
              tabIndex="0"
              role="textbox"
              aria-label="Rooms To Go Credit Options"
            />
          </InfoModal>
        )}
      </>
    )
  }
  if (linkData?.PredefinedComponentModal) {
    return (
      <MuiLink
        className={classNames(classes.link, className, { 'disable-underline': disableUnderline })}
        {...linkProps}
        data-testid={linkData?.testId}
        style={{
          textDecoration: 'none',
          cursor: 'pointer',
        }}
        onClick={() => actions.genericModal({ data: linkData })}
        aria-label={linkData?.AlternateDescription}
      >
        {children || linkData?.DisplayText || linkData?.Title}
        {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
      </MuiLink>
    )
  }

  if (linkData?.PredefinedLink) {
    return (
      <MuiLink
        href={determinePredefinedLinkParams(linkData?.PredefinedLink, linkData?.ExternalUrl)}
        className={classNames(classes.link, className)}
        {...linkProps}
        data-testid={linkData?.testId}
        aria-label={linkData?.AlternateDescription}
      >
        {determineText(children, linkData)}
        {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
        {linkProps?.target === '_blank' && <span className="hide508">(opens in new window)</span>}
      </MuiLink>
    )
  }

  if (linkData?.ExternalUrl) {
    if (isSynchronyApplyLink(linkData?.ExternalUrl)) {
      return (
        <ApplyButton
          key={linkData?.id}
          url={
            isSynchronyWithQueriesLink(linkData?.ExternalUrl)
              ? `${linkData?.ExternalUrl.substring(linkData?.ExternalUrl.indexOf('&'))}&modalapply=true`
              : `&modalapply=true`
          }
          aria-label={linkData?.AlternateDescription}
          action="link click"
          className={classNames(classes.link, className)}
          data-testid={linkData?.testId}
          {...linkProps}
        >
          {determineText(children, linkData)}
          {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
          {linkProps?.target === '_blank' && <span className="hide508">(opens in new window)</span>}
        </ApplyButton>
      )
    }
    return (
      <MuiLink
        href={linkData?.ExternalUrl}
        className={classNames(classes.link, className)}
        {...linkProps}
        data-testid={linkData?.testId}
        aria-label={linkData?.AlternateDescription}
      >
        {determineText(children, linkData)}
        {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
        {linkProps?.target === '_blank' && <span className="hide508">(opens in new window)</span>}
      </MuiLink>
    )
  }
  let internalUrl = growthbookLinkUrl?.internalUrl ?? linkData?.InternalUrl
  internalUrl = (internalUrl || '/').startsWith('/') ? internalUrl : `/${internalUrl}`
  // Since we only run the static query if we are not doing a static build we should still use client side routing if allPages is length 0
  if (canClientSideRoute(location?.pathname, internalUrl)) {
    // Custom onClick handler for scroll preservation
    const handleClick = e => {
      if (preserveScrollOnNavigate && typeof window !== 'undefined') {
        e.preventDefault()
        // Fire analytics if needed
        if (linkProps.onClick) linkProps.onClick(e)
        navigate(linkData?.InternalUrl, {
          state: { scrollPosition: window.scrollY },
        })
      } else if (linkProps.onClick) {
        linkProps.onClick(e)
      }
    }
    return (
      <MuiLink
        to={internalUrl}
        component={GatsbyLink}
        className={classNames(classes.link, className, { 'disable-underline': disableUnderline })}
        {...linkProps}
        data-testid={linkData?.testId}
        aria-label={linkData?.AlternateDescription}
        onClick={handleClick}
      >
        {determineText(children, linkData)}
        {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
        {linkProps?.target === '_blank' && <span className="hide508">(opens in new window)</span>}
      </MuiLink>
    )
  }
  return (
    <MuiLink
      href={internalUrl}
      className={classNames(classes.link, className, { 'disable-underline': disableUnderline })}
      {...linkProps}
      data-testid={linkData?.testId}
      aria-label={linkData?.AlternateDescription}
      sx={{
        textDecoration: 'none',
      }}
    >
      {determineText(children, linkData)}
      {arrowNext && <NavigateNextIcon className="link-arrow-next" />}
      {linkProps?.target === '_blank' && <span className="hide508">(opens in new window)</span>}
    </MuiLink>
  )
}

StrapiLink.propTypes = {
  data: PropTypes.object,
  trackingData: PropTypes.shape({
    event: PropTypes.string,
    ecommerce: PropTypes.object,
    category: PropTypes.string,
    action: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
  }),
  disableUnderline: PropTypes.bool,
  children: PropTypes.node,
  arrowNext: PropTypes.bool,
  className: PropTypes.string,
  preserveScrollOnNavigate: PropTypes.bool,
}

export default StrapiLink

// ? https://github.com/reactjs/reactjs.org/issues/2120
export const ButtonLink = forwardRef((props, ref) => <StrapiLink ref={ref} {...props} />)
ButtonLink.displayName = 'ButtonLink'

/**
 * Helper
 * @param {{ Name?: string; Title?: string; Heading?: string; }} data Strapi data
 * @param {object} link Strapi Link data
 * @param {object} trackingData Additional tracking data
 * @returns StrapiLink props
 */
export const getLinkProps = (data, link, trackingData = {}) => ({
  component: ButtonLink,
  data: link,
  trackingData: {
    category: 'promotion',
    action: 'click',
    label: data?.Name || data?.Heading || data?.Title,
    ...trackingData,
  },
})

const MattressFinanceModalQuery = graphql`
  query MattressFinanceModalQuery {
    strapiHtml(Title: { eq: "Mattress Finance Modal Content" }) {
      Title
      HTML
    }
  }
`

export const StrapiLinkFragment = graphql`
  fragment StrapiLinkFragment on StrapiLink {
    id
    testId
    Title
    DisplayText
    MobileDisplayText
    InternalUrl
    ExternalUrl
    Color {
      ColorHex
    }
    Target
    AlternateDescription
    ShowLearnMoreFinanceModal
    PredefinedComponentModal
    CustomLinkGrowthbookId
    PredefinedLink
  }
`
